/**
 * special-framed-image: [data-special-frame] with .frame-image
 * special-framed-button: [data-sfb-button-frame]
 */
(function () {
  const MOBILE_MQ = window.matchMedia("(max-width: 699px)");

  function compact(s) {
    return s.replace(/\s+/g, " ").trim();
  }

  function buildImagePaths(r, gap) {
    const W = 1000;
    const H = 1000;

    const outerPath = `
      M ${r} 0
      H ${W - r}
      C ${W - r} 0 ${W - r} ${r} ${W} ${r}
      V ${H - r}
      C ${W - r} ${H - r} ${W - r} ${H} ${W - r} ${H}
      H ${r}
      C ${r} ${H} ${r} ${H - r} 0 ${H - r}
      V ${r}
      C ${r} ${r} ${r} 0 ${r} 0
      Z
    `;

    const ir = r;
    const ig = gap;

    const innerPath = `
      M ${ir + ig} ${ig}
      H ${W - ir - ig}
      C ${W - ir - ig} ${ig}
        ${W - ir - ig} ${ir + ig}
        ${W - ig} ${ir + ig}
      V ${H - ir - ig}
      C ${W - ir - ig} ${H - ir - ig}
        ${W - ir - ig} ${H - ig}
        ${W - ir - ig} ${H - ig}
      H ${ir + ig}
      C ${ir + ig} ${H - ig}
        ${ir + ig} ${H - ir - ig}
        ${ig} ${H - ir - ig}
      V ${ir + ig}
      C ${ir + ig} ${ir + ig}
        ${ir + ig} ${ig}
        ${ir + ig} ${ig}
      Z
    `;

    return { outerPath: compact(outerPath), innerPath: compact(innerPath) };
  }

  function buildButtonPaths(r, gap, buttonW, buttonH) {
    const outerRight = buttonW - r;
    const outerBottom = buttonH - r;
    const outer =
      "M " +
      r +
      " 0 H " +
      outerRight +
      " C " +
      outerRight +
      " 0 " +
      outerRight +
      " " +
      r +
      " " +
      buttonW +
      " " +
      r +
      " V " +
      outerBottom +
      " C " +
      outerRight +
      " " +
      outerBottom +
      " " +
      outerRight +
      " " +
      buttonH +
      " " +
      outerRight +
      " " +
      buttonH +
      " H " +
      r +
      " C " +
      r +
      " " +
      buttonH +
      " " +
      r +
      " " +
      outerBottom +
      " 0 " +
      outerBottom +
      " V " +
      r +
      " C " +
      r +
      " " +
      r +
      " " +
      r +
      " 0 " +
      r +
      " 0 Z";

    const innerStart = r + gap;
    const innerRight = buttonW - r - gap;
    const innerEdgeRight = buttonW - gap;
    const innerBottom = buttonH - r - gap;
    const innerEdgeBottom = buttonH - gap;
    const inner =
      "M " +
      innerStart +
      " " +
      gap +
      " H " +
      innerRight +
      " C " +
      innerRight +
      " " +
      gap +
      " " +
      innerRight +
      " " +
      innerStart +
      " " +
      innerEdgeRight +
      " " +
      innerStart +
      " V " +
      innerBottom +
      " C " +
      innerRight +
      " " +
      innerBottom +
      " " +
      innerRight +
      " " +
      innerEdgeBottom +
      " " +
      innerRight +
      " " +
      innerEdgeBottom +
      " H " +
      innerStart +
      " C " +
      innerStart +
      " " +
      innerEdgeBottom +
      " " +
      innerStart +
      " " +
      innerBottom +
      " " +
      gap +
      " " +
      innerBottom +
      " V " +
      innerStart +
      " C " +
      innerStart +
      " " +
      innerStart +
      " " +
      innerStart +
      " " +
      gap +
      " " +
      innerStart +
      " " +
      gap +
      " Z";

    return { outer: outer, inner: inner };
  }

  function readImageFrameShape(frame) {
    const styles = getComputedStyle(frame);
    const useMobile = MOBILE_MQ.matches && frame.hasAttribute("data-corner-radius-mobile");

    const r = useMobile
      ? parseFloat(frame.dataset.cornerRadiusMobile)
      : parseFloat(styles.getPropertyValue("--corner-radius"));
    const gap = useMobile
      ? parseFloat(frame.dataset.innerGapMobile)
      : parseFloat(styles.getPropertyValue("--inner-gap"));
    const borderWidth = useMobile
      ? parseFloat(frame.dataset.borderWidthMobile)
      : parseFloat(styles.getPropertyValue("--border-width"));

    return {
      r: r || 20,
      gap: gap || 10,
      borderWidth: borderWidth || 2,
      borderColor: styles.getPropertyValue("--border-color").trim() || "#2a2a2a",
    };
  }

  function updateImageFrame(frame) {
    if (!frame.querySelector(".frame-image")) return;

    const outer = frame.querySelector(".outer-border");
    const inner = frame.querySelector(".inner-border");
    const shape = frame.querySelector(".shape-path");
    const image = frame.querySelector(".frame-image");

    if (!outer || !inner || !shape || !image) return;

    const { r, gap, borderWidth, borderColor } = readImageFrameShape(frame);
    const signature = [MOBILE_MQ.matches ? "m" : "d", r, gap, borderWidth, borderColor].join("|");

    if (frame.dataset.specialFrameSignature === signature) return;
    frame.dataset.specialFrameSignature = signature;

    frame.style.setProperty("--border-width", String(borderWidth));

    const { outerPath, innerPath } = buildImagePaths(r, gap);

    shape.setAttribute("d", outerPath);
    outer.setAttribute("d", outerPath);
    inner.setAttribute("d", innerPath);
    outer.setAttribute("stroke", borderColor);
    inner.setAttribute("stroke", borderColor);
    outer.setAttribute("stroke-width", String(borderWidth));
    inner.setAttribute("stroke-width", String(borderWidth));

    let imageUrl = getComputedStyle(frame).getPropertyValue("--image").trim();
    imageUrl = imageUrl.replace(/^url\(["']?/, "").replace(/["']?\)$/, "");

    if (imageUrl) {
      image.setAttribute("href", imageUrl);
      image.setAttributeNS("http://www.w3.org/1999/xlink", "href", imageUrl);
      frame.dataset.specialFrameImageBound = "true";
    }
  }

  function bindImageFrame(frame) {
    updateImageFrame(frame);

    if (frame.dataset.specialFrameBound === "true") return;
    frame.dataset.specialFrameBound = "true";
  }

  function readButtonShape(button) {
    const styles = getComputedStyle(button);
    const useMobile = MOBILE_MQ.matches && button.hasAttribute("data-corner-radius-mobile");

    const r = useMobile
      ? parseFloat(button.dataset.cornerRadiusMobile)
      : parseFloat(button.dataset.cornerRadius) ||
        parseFloat(styles.getPropertyValue("--sfb-button-corner-radius"));
    const gap = useMobile
      ? parseFloat(button.dataset.innerGapMobile)
      : parseFloat(button.dataset.innerGap) || parseFloat(styles.getPropertyValue("--sfb-button-inner-gap"));
    const borderWidth = useMobile
      ? parseFloat(button.dataset.borderWidthMobile)
      : parseFloat(button.dataset.borderWidth) ||
        parseFloat(styles.getPropertyValue("--sfb-button-border-width"));

    return {
      r: r || 8,
      gap: gap || 4,
      borderWidth: borderWidth || 2,
      buttonW: parseFloat(styles.getPropertyValue("--sfb-button-width")) || 570,
      buttonH: parseFloat(styles.getPropertyValue("--sfb-button-height")) || 72,
    };
  }

  function updateButton(button) {
    const fill = button.querySelector("[data-sfb-btn-fill]");
    const outer = button.querySelector("[data-sfb-btn-outer]");
    const inner = button.querySelector("[data-sfb-btn-inner]");
    if (!fill || !outer || !inner) return;

    const { r, gap, borderWidth, buttonW, buttonH } = readButtonShape(button);
    const signature = [MOBILE_MQ.matches ? "m" : "d", r, gap, borderWidth, buttonW, buttonH].join("|");

    if (button.dataset.sfbButtonSignature === signature) return;
    button.dataset.sfbButtonSignature = signature;

    button.style.setProperty("--sfb-button-corner-radius", String(r));
    button.style.setProperty("--sfb-button-inner-gap", String(gap));
    button.style.setProperty("--sfb-button-border-width", String(borderWidth));

    const paths = buildButtonPaths(r, gap, buttonW, buttonH);

    fill.setAttribute("d", paths.outer);
    outer.setAttribute("d", paths.outer);
    inner.setAttribute("d", paths.inner);
  }

  function bindButton(button) {
    updateButton(button);

    if (button.dataset.sfbButtonBound === "true") return;
    button.dataset.sfbButtonBound = "true";
  }

  function refreshButtons() {
    document.querySelectorAll("[data-sfb-button-frame]").forEach((button) => {
      button.dataset.sfbButtonSignature = "";
      updateButton(button);
    });
  }

  function refreshImageFrames() {
    document.querySelectorAll("[data-special-frame]").forEach((frame) => {
      frame.dataset.specialFrameSignature = "";
      updateImageFrame(frame);
    });
  }

  function init(root) {
    const scope = root && root.nodeType ? root : document;
    scope.querySelectorAll("[data-special-frame]").forEach(bindImageFrame);
    scope.querySelectorAll("[data-sfb-button-frame]").forEach(bindButton);
  }

  let resizeTimer;
  function onViewportChange() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      refreshImageFrames();
      refreshButtons();
    }, 100);
  }

  window.initSpecialFrames = init;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(document));
  } else {
    init(document);
  }

  window.addEventListener("resize", onViewportChange);
  if (typeof MOBILE_MQ.addEventListener === "function") {
    MOBILE_MQ.addEventListener("change", onViewportChange);
  } else if (typeof MOBILE_MQ.addListener === "function") {
    MOBILE_MQ.addListener(onViewportChange);
  }

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target || document);
  });

  if (typeof Shopify !== "undefined" && Shopify.designMode) {
    window.setInterval(() => {
      refreshImageFrames();
      refreshButtons();
    }, 150);
  }
})();
